/**
 * BankConsentPanel - Pre-consent information panel for Akahu transparency
 * 
 * Displays information about:
 * - How bank connection works
 * - What data is accessed
 * - User control and security
 * 
 * Required for Akahu Open Banking compliance
 */
import { Card, CardContent } from "../ui/card";
import { Link } from "react-router-dom";
import { Shield, Info, CheckCircle2, ExternalLink } from "lucide-react";

export function BankConsentPanel({ compact = false }) {
  return (
    <Card className="border-[#1a4d3e]/20 bg-[#1a4d3e]/5 dark:bg-[#1a4d3e]/10" data-testid="bank-consent-panel">
      <CardContent className={compact ? "p-4" : "p-6"}>
        <div className="flex items-start gap-3 mb-4">
          <div className="p-2 bg-[#1a4d3e]/10 rounded-lg flex-shrink-0">
            <Shield className="w-5 h-5 text-[#1a4d3e]" />
          </div>
          <div>
            <h3 className={`font-semibold text-foreground ${compact ? "text-sm" : ""}`}>
              Connect your bank securely
            </h3>
          </div>
        </div>
        
        <p className={`text-muted-foreground mb-4 ${compact ? "text-xs" : "text-sm"}`}>
          ARA Wealth uses Akahu to securely connect to your bank and import your transactions.
        </p>
        
        <div className="space-y-4">
          {/* What happens when you connect */}
          <div>
            <p className={`font-medium text-foreground mb-2 ${compact ? "text-xs" : "text-sm"}`}>
              When you connect your bank:
            </p>
            <ul className={`space-y-1.5 ${compact ? "text-xs" : "text-sm"}`}>
              {[
                "You will be redirected to Akahu to authorise access.",
                "You can choose which accounts to share.",
                "Your internet banking login details are never shared with ARA Wealth."
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-muted-foreground">
                  <CheckCircle2 className="w-3.5 h-3.5 text-[#1a4d3e] mt-0.5 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          
          {/* What data is used for */}
          <div>
            <p className={`font-medium text-foreground mb-2 ${compact ? "text-xs" : "text-sm"}`}>
              Your data will be used to:
            </p>
            <ul className={`space-y-1.5 ${compact ? "text-xs" : "text-sm"}`}>
              {[
                "Import transactions",
                "Categorise spending",
                "Provide budgeting insights"
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-muted-foreground">
                  <CheckCircle2 className="w-3.5 h-3.5 text-[#1a4d3e] mt-0.5 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
        
        {/* Learn more link */}
        <div className="mt-4 pt-4 border-t border-[#1a4d3e]/10">
          <Link 
            to="/security/open-banking"
            className={`inline-flex items-center gap-1.5 text-[#1a4d3e] hover:underline font-medium ${compact ? "text-xs" : "text-sm"}`}
          >
            <Info className="w-4 h-4" />
            Learn how ARA Wealth securely connects to your bank
            <ExternalLink className="w-3 h-3" />
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * BankConnectionDisclaimer - Small disclosure text below connect button
 */
export function BankConnectionDisclaimer({ compact = false }) {
  return (
    <p className={`text-muted-foreground text-center ${compact ? "text-xs" : "text-xs"}`} data-testid="bank-connection-disclaimer">
      By continuing you will be redirected to Akahu to authorise access to your selected bank accounts.
    </p>
  );
}

/**
 * BankConnectionSuccessBanner - Shown after successful connection
 */
export function BankConnectionSuccessBanner({ onDismiss }) {
  return (
    <div 
      className="p-4 rounded-xl bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800"
      data-testid="bank-connection-success-banner"
    >
      <div className="flex items-start gap-3">
        <div className="p-1.5 bg-emerald-100 dark:bg-emerald-900/50 rounded-full flex-shrink-0">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
        </div>
        <div className="flex-1">
          <h4 className="font-medium text-emerald-900 dark:text-emerald-100 mb-1">
            Bank successfully connected via Akahu.
          </h4>
          <p className="text-sm text-emerald-700 dark:text-emerald-300 mb-2">
            ARA Wealth will now securely import your transactions.
          </p>
          <p className="text-xs text-emerald-600 dark:text-emerald-400">
            You can disconnect your bank at any time from the Integrations page.
          </p>
        </div>
        {onDismiss && (
          <button 
            onClick={onDismiss}
            className="text-emerald-500 hover:text-emerald-700 p-1"
            aria-label="Dismiss"
          >
            ×
          </button>
        )}
      </div>
    </div>
  );
}

export default BankConsentPanel;
